import { getSupabaseClient } from '../lib/supabase';
import type {
  FinalizedPhotoUpload,
  PhotoUploadReservation,
  PreparedWebPhoto,
} from '../types/uploads';

export const MAX_WEB_PHOTO_BYTES = 50 * 1024 * 1024;
export const MAX_WEB_PHOTO_BATCH = 10;

const SUPPORTED_PHOTO_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
]);

function extensionForFile(file: File): string {
  const name = file.name.toLowerCase().split('?')[0];
  if (name.endsWith('.png')) return 'png';
  if (name.endsWith('.webp')) return 'webp';
  if (name.endsWith('.heic')) return 'heic';
  return 'jpg';
}

function contentTypeForFile(file: File): string {
  const raw = String(file.type || '').trim().toLowerCase();
  if (raw === 'image/jpg') return 'image/jpeg';
  if (SUPPORTED_PHOTO_TYPES.has(raw)) return raw;

  const extension = extensionForFile(file);
  if (extension === 'png') return 'image/png';
  if (extension === 'webp') return 'image/webp';
  if (extension === 'heic') return 'image/heic';
  if (/\.(jpe?g)$/i.test(file.name)) return 'image/jpeg';
  return raw;
}

function createLocalClientId(): string {
  const random = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);
  return `web-${Date.now()}-${random}`;
}

async function readImageDimensions(file: File): Promise<{ width: number | null; height: number | null }> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file);
      const dimensions = { width: bitmap.width || null, height: bitmap.height || null };
      bitmap.close();
      return dimensions;
    } catch {
      // Fall through to browser Image decoding below.
    }
  }

  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      const dimensions = {
        width: image.naturalWidth || null,
        height: image.naturalHeight || null,
      };
      URL.revokeObjectURL(url);
      resolve(dimensions);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({ width: null, height: null });
    };
    image.src = url;
  });
}

export async function prepareWebPhoto(file: File): Promise<PreparedWebPhoto> {
  const contentType = contentTypeForFile(file);
  if (!SUPPORTED_PHOTO_TYPES.has(contentType)) {
    throw new Error(`${file.name}: use JPEG, PNG, WebP, or HEIC.`);
  }
  if (file.size <= 0) throw new Error(`${file.name}: the file is empty.`);
  if (file.size > MAX_WEB_PHOTO_BYTES) {
    throw new Error(`${file.name}: photos must be 50 MB or smaller.`);
  }

  const dimensions = await readImageDimensions(file);
  return {
    localClientId: createLocalClientId(),
    file,
    fileName: file.name || 'gathrd-photo.jpg',
    contentType,
    extension: extensionForFile(file),
    width: dimensions.width,
    height: dimensions.height,
  };
}

export async function reserveWebPhotoUploads(
  eventId: string,
  photos: PreparedWebPhoto[],
): Promise<Map<string, PhotoUploadReservation>> {
  if (photos.length === 0) return new Map();
  if (photos.length > 25) throw new Error('At most 25 photos can be signed in one request.');

  const client = getSupabaseClient();
  const { data, error } = await client.functions.invoke('create-photo-upload-url', {
    body: {
      items: photos.map((photo) => ({
        eventId,
        localClientId: photo.localClientId,
        fileName: photo.fileName,
        contentType: photo.contentType,
        extension: photo.extension,
        fileSize: photo.file.size,
        width: photo.width ?? undefined,
        height: photo.height ?? undefined,
        expiresIn: 3600,
        objectKind: 'display',
        cachePolicy: 'media-cache-v1',
        metadata: {
          source: 'gathrd-web',
          client: 'web-parity-p3',
          fileName: photo.fileName,
        },
      })),
    },
  });
  if (error) throw error;

  const response = data as { items?: Array<Record<string, unknown>> } | null;
  const result = new Map<string, PhotoUploadReservation>();
  for (const raw of response?.items ?? []) {
    const localClientId = String(raw.localClientId ?? '').trim();
    const uploadUrl = String(raw.uploadUrl ?? '').trim();
    const key = String(raw.key ?? raw.storagePath ?? '').trim();
    const photo = raw.photo as { id?: string } | null | undefined;
    const photoId = String(photo?.id ?? raw.photoId ?? '').trim();
    if (raw.ok !== true || !localClientId || !uploadUrl || !key || !photoId) continue;
    result.set(localClientId, {
      ok: true,
      photoId,
      localClientId,
      uploadUrl,
      uploadUrlExpiresAt: String(raw.uploadUrlExpiresAt ?? '') || null,
      bucket: String(raw.bucket ?? '') || null,
      key,
      storagePath: String(raw.storagePath ?? key),
      contentType: String(raw.contentType ?? 'image/jpeg'),
      cacheControl: String(raw.cacheControl ?? '') || null,
    });
  }
  return result;
}

export function putWebPhotoToR2(
  photo: PreparedWebPhoto,
  reservation: PhotoUploadReservation,
  onProgress?: (progress: number) => void,
): Promise<string | null> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let lastReportedProgress = -1;
    let lastReportedAt = 0;

    const reportProgress = (progress: number) => {
      const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
      const shouldReport =
        progress >= 100 ||
        lastReportedProgress < 0 ||
        progress - lastReportedProgress >= 2 ||
        now - lastReportedAt >= 120;
      if (!shouldReport) return;
      lastReportedProgress = progress;
      lastReportedAt = now;
      onProgress?.(progress);
    };

    xhr.open('PUT', reservation.uploadUrl, true);
    xhr.setRequestHeader('Content-Type', reservation.contentType || photo.contentType);
    if (reservation.cacheControl) xhr.setRequestHeader('Cache-Control', reservation.cacheControl);

    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable) return;
      const progress = Math.max(0, Math.min(100, Math.round((event.loaded / event.total) * 100)));
      reportProgress(progress);
    };

    xhr.onerror = () => {
      reject(new Error('R2 upload failed. The browser may be blocked by media CORS or the network connection.'));
    };
    xhr.onabort = () => reject(new Error('Upload cancelled.'));
    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new Error(`R2 upload failed with status ${xhr.status}.`));
        return;
      }
      reportProgress(100);
      resolve(xhr.getResponseHeader('etag') || xhr.getResponseHeader('ETag'));
    };

    xhr.send(photo.file);
  });
}

export async function finalizeWebPhotoUpload(
  photo: PreparedWebPhoto,
  reservation: PhotoUploadReservation,
  r2Etag: string | null,
): Promise<FinalizedPhotoUpload> {
  const client = getSupabaseClient();
  const { data, error } = await client.functions.invoke('finalize-photo-upload', {
    body: {
      photoId: reservation.photoId,
      originalStoragePath: reservation.storagePath,
      displayStoragePath: reservation.storagePath,
      width: photo.width,
      height: photo.height,
      fileSize: photo.file.size,
      r2Etag: r2Etag || undefined,
      queueWatermark: true,
      metadata: {
        source: 'gathrd-web',
        client: 'web-parity-p3',
        localClientId: photo.localClientId,
        fileName: photo.fileName,
      },
    },
  });
  if (error) throw error;

  const response = data as {
    ok?: boolean;
    error?: string;
    photoId?: string;
    photo?: { id?: string; moderation_status?: string | null };
  } | null;
  if (!response?.ok) {
    throw new Error(response?.error || 'Gathrd could not finalize this photo upload.');
  }

  return {
    photoId: String(response.photo?.id ?? response.photoId ?? reservation.photoId),
    moderationStatus: response.photo?.moderation_status ?? 'pending',
  };
}
